import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "@/app/routes/paths";
import type { Trip } from "@/entities/trip";
import { useTrip } from "@/features/trips";
import {
  DECLINE_REASON_MAX,
  DECLINE_REASON_SUGGESTIONS,
  formatOfferCountdown,
  isOfferExpired,
  isUsableDeclineReason,
} from "@/features/trips/lib/offerPolicy";
import { colors, semantic } from "@/shared/design-system";
import { formatXof } from "@/shared/format";
import { AppModal, AppText, Input, Spinner, useDialog } from "@/shared/ui";
import { offerWaveKey, recallEvent } from "@/shared/realtime";

/**
 * L'offre de course, présentée à la Main — contrat du 2026-08-28, §5.
 *
 * ## Elle s'expire toute seule, et c'est indispensable
 * **Aucun signal n'arrive quand une offre expire** — ni socket, ni push.
 * `TRIP_OFFER_EXPIRED` part vers `trip:{id}`, une salle où la Main sollicitée
 * n'est jamais entrée. Cet écran doit donc se fermer de lui-même sur
 * `expires_at`, sans rien attendre.
 *
 * ## Le compte à rebours est calé sur l'échéance ABSOLUE
 * Jamais sur `timeout_minutes`, qui est le délai nominal accordé à l'émission :
 * entre les deux il y a la latence de diffusion, jusqu'à cinq secondes, plus le
 * trajet de la notification. Un décompte calé sur le délai nominal surestime
 * toujours, et la Main croit avoir du temps alors que le refus l'attend.
 *
 * ⚠️ Depuis le 2026-09-02, `timeout_minutes` est en plus devenu FAUX : le délai
 * est passé à quatre-vingt-dix secondes, et ce champ vaut `2` par arrondi.
 * L'afficher annoncerait deux minutes à une Main qui en a une et demie. Le
 * contrat rend `timeout_seconds` pour l'exprimer, mais c'est `expires_at` qui
 * fait foi — et qui pilote ce décompte depuis le premier jour.
 *
 * ## Les détails viennent de l'API, pas de l'événement
 * L'offre ne porte AUCUNE donnée métier — ni adresse, ni montant, ni nom. Tout
 * ce qui s'affiche ici vient de `GET /trips/{id}`.
 */
export function OfferSheet({
  trip,
  onClose,
  onAccept,
  onDecline,
  isResponding,
}: {
  trip: Trip;
  onClose: () => void;
  onAccept: () => void;
  onDecline: (reason: string) => void;
  isResponding: boolean;
}) {
  const navigate = useNavigate();
  const { alert } = useDialog();
  const tripId = trip.id ?? "";

  // Détails complets : l'offre n'en porte aucun.
  const details = useTrip(tripId, Boolean(tripId));
  const full = details.data ?? trip;

  const [now, setNow] = useState(() => Date.now());
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState("");
  const [showReasonError, setShowReasonError] = useState(false);

  // Un tick par seconde : le décompte est la seule information qui bouge.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  const expired = isOfferExpired(trip.offer_expires_at, now);
  const countdown = formatOfferCountdown(trip.offer_expires_at, now);

  // Auto-fermeture : personne ne viendra nous le dire.
  useEffect(() => {
    if (!expired) return;
    onClose();
  }, [expired, onClose]);

  const stops = useMemo(
    () => [...(full.stops ?? [])].sort((a, b) => (a.stop_index ?? 0) - (b.stop_index ?? 0)),
    [full.stops],
  );

  const handleAccept = () => {
    // Dernière garde avant l'envoi : une push sans TTL peut avoir ouvert cet
    // écran bien après coup (FCM applique son défaut de quatre semaines).
    if (isOfferExpired(trip.offer_expires_at, Date.now())) {
      void alert({ tone: "info", title: "Offre expirée", message: "Cette course n'est plus disponible." });
      onClose();
      return;
    }
    onAccept();
  };

  const handleDecline = () => {
    if (!isUsableDeclineReason(reason)) {
      setShowReasonError(true);
      return;
    }
    onDecline(reason);
  };

  const reward = full.estimated_total ?? null;

  // Combien de Mains ont reçu CETTE offre (§1 du contrat du 2026-09-02).
  // Retenu au passage de l'événement — aucune route ne le porte.
  const waveSize = recallEvent<number>(offerWaveKey(trip.id ?? ""));

  return (
    <AppModal
      visible
      onClose={onClose}
      title="Nouvelle course"
      subtitle={
        countdown
          ? `Réponds avant ${countdown}`
          : "Réponds rapidement — cette offre est temporaire."
      }
      // Fermer sur le fond ferait perdre l'offre par mégarde, sans réponse :
      // ignorer une offre immobilise la Main jusqu'à expiration.
      dismissOnBackdrop={false}
      size="tall"
    >
      <div className="flex flex-col gap-4 px-5 pb-6 pt-1">
        {/* Le décompte, gros et seul élément mouvant. */}
        <div
          className={`flex items-center justify-center rounded-card py-3 ${
            expired ? "bg-sos-200" : "bg-or-50"
          }`}
        >
          <AppText variant="h2" color={expired ? colors.sos600 : colors.or700}>
            {countdown ?? "—"}
          </AppText>
        </div>

        {/* Elles sont plusieurs dessus, et la première qui accepte l'emporte.
            Le dire change la lecture d'un échec : sans cela, un refus
            d'acceptation ressemble à une panne de l'application. Affiché
            seulement quand le serveur a donné le nombre — on ne suppose pas
            qu'il y en a trois. */}
        {waveSize != null && waveSize > 1 && (
          <div className="flex flex-row items-start gap-1.5 rounded-input bg-gray-50 px-3 py-2">
            <AppText variant="tiny" color={colors.gray500} aria-hidden>
              ⚡
            </AppText>
            <AppText as="p" variant="tiny" color={colors.gray500} className="flex-1">
              Proposée à {waveSize} Mains en même temps — la première qui accepte l&apos;emporte.
            </AppText>
          </div>
        )}

        {details.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner size={28} color={colors.or500} />
          </div>
        ) : (
          <>
            {reward != null && (
              <div className="flex flex-row items-center justify-between">
                <AppText variant="label" color={colors.gray500}>
                  Montant estimé
                </AppText>
                <AppText variant="h4" color={colors.navy800}>
                  {formatXof(reward)}
                </AppText>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <AppText as="p" variant="eyebrow" color={colors.gray500}>
                Itinéraire
              </AppText>
              {stops.map((stop, i) => (
                <div key={stop.id ?? i} className="flex flex-row gap-2.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100">
                    <AppText variant="miniBold" color={colors.navy800}>
                      {i + 1}
                    </AppText>
                  </span>
                  <AppText variant="small" color={colors.navy800} className="min-w-0 flex-1">
                    {stop.location?.address ?? "Point sur la carte"}
                  </AppText>
                </div>
              ))}
              {full.delivery_address?.line1 && (
                <div className="flex flex-row gap-2.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50">
                    <AppText variant="miniBold" color={colors.emerald700}>
                      ✓
                    </AppText>
                  </span>
                  <AppText variant="small" color={colors.navy800} className="min-w-0 flex-1">
                    {full.delivery_address.line1}
                  </AppText>
                </div>
              )}
            </div>
          </>
        )}

        {refusing ? (
          <div className="flex flex-col gap-2">
            <Input
              label="Pourquoi refuses-tu ?"
              placeholder="Motif du refus"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={DECLINE_REASON_MAX}
              error={
                showReasonError && !isUsableDeclineReason(reason)
                  ? "Indique un motif d'au moins 3 caractères."
                  : undefined
              }
            />
            <div className="flex flex-row flex-wrap gap-1.5">
              {DECLINE_REASON_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setReason(s)}
                  className="rounded-full bg-gray-100 px-3 py-1.5"
                >
                  <AppText variant="mini" color={colors.gray500}>
                    {s}
                  </AppText>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleDecline}
              disabled={isResponding}
              className="mt-1 flex h-[52px] w-full items-center justify-center rounded-btn bg-sos-600 disabled:opacity-60"
            >
              <AppText variant="button" color={colors.white}>
                Confirmer le refus
              </AppText>
            </button>
            <button
              type="button"
              onClick={() => setRefusing(false)}
              className="flex h-11 w-full items-center justify-center"
            >
              <AppText variant="miniBold" color={colors.gray500}>
                Retour
              </AppText>
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleAccept}
              // Désactivé DÈS le premier appui : `accept` n'accepte aucune clé
              // d'idempotence, un second envoi est indiscernable d'un doublon.
              disabled={isResponding || expired}
              className="flex h-[54px] w-full items-center justify-center gap-2 rounded-btn bg-or-500 disabled:opacity-60"
            >
              {isResponding && <Spinner size={18} color={semantic.contentOnBrand} />}
              <AppText variant="button" color={semantic.contentOnBrand}>
                {isResponding ? "Envoi…" : "Accepter la course"}
              </AppText>
            </button>
            <button
              type="button"
              onClick={() => setRefusing(true)}
              disabled={isResponding}
              className="flex h-[52px] w-full items-center justify-center rounded-btn bg-gray-100"
            >
              <AppText variant="button" color={colors.navy800}>
                Refuser
              </AppText>
            </button>
          </div>
        )}

        <AppText as="p" variant="tiny" color={colors.gray500} className="block text-center">
          {/* On ne promet RIEN sur la suite : refuser ne relance pas cette
              course, la reprise repart de la plus ancienne en attente et peut
              n'arriver qu'une minute plus tard. */}
          Sans réponse de ta part, l&apos;offre expire d&apos;elle-même.
        </AppText>

        {/* Raccourci de secours si la course a déjà été acceptée ailleurs. */}
        {full.status && full.status !== "AGENT_OFFER_PENDING" && (
          <button
            type="button"
            onClick={() => navigate(paths.agentTrip(tripId))}
            className="flex h-11 w-full items-center justify-center"
          >
            <AppText variant="miniBold" color={colors.or700}>
              Ouvrir la course
            </AppText>
          </button>
        )}
      </div>
    </AppModal>
  );
}
